import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { sessionsApi } from "../api/sessions.api.js";
import { credentialsApi } from "../api/credentials.api.js";
import { unwrapError } from "../api/client.js";
import { Card, DangerButton, ErrorState, Icon, SkeletonList, PageHeader, Section, StatusChip, CredentialChip, VenueChip, colors, } from "../components/ui/index.js";
import { useConfirm } from "../components/ui/ConfirmProvider.js";
import { useToast } from "../components/ui/ToastProvider.js";
import { SessionTimeline } from "../components/sessions/SessionTimeline.js";
import { CourtGrid, CourtGridKey } from "../components/sessions/CourtGrid.js";
import { venueCourtCount } from "../lib/venue-names.js";
import { formatSessionSubline } from "../lib/format-session.js";
const SKIP_REASON_TEXT = {
    no_credit_capacity: "No credential had enough credit capacity at the bookable moment.",
    no_credentials_with_access: "No credentials with access to this venue at the requested time.",
    permission_revoked: "Permissions were revoked before the session became bookable.",
    no_availability: "No matching slots were available at the time of booking.",
    account_disabled: "The tenant account was disabled before this session could be booked.",
    no_single_credential_for_venue: "No single credential had both access and enough credit to cover this venue, so it was skipped.",
};
function formatFullTime(startIso, endIso) {
    const start = new Date(startIso);
    const end = new Date(endIso);
    const day = start.toLocaleDateString(undefined, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
    });
    const fmtTime = (d) => d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return `${day} · ${fmtTime(start)} – ${fmtTime(end)}`;
}
export function SessionDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { confirm } = useConfirm();
    const { showToast } = useToast();
    const detailQ = useQuery({
        queryKey: ["session", id],
        queryFn: () => sessionsApi.detail(id ?? ""),
        enabled: Boolean(id),
    });
    const credsQ = useQuery({
        queryKey: ["credentials"],
        queryFn: credentialsApi.list,
    });
    const cancel = useMutation({
        mutationFn: () => sessionsApi.cancel(id ?? ""),
        onSuccess: (data) => {
            const failed = data.failedCancels ?? 0;
            const detail = failed > 0 ? ` (${failed} venue cancellation${failed === 1 ? "" : "s"} failed)` : "";
            showToast(`Session cancelled${detail}.`, failed > 0 ? "error" : "success");
            void qc.invalidateQueries({ queryKey: ["sessions"] });
            void qc.invalidateQueries({ queryKey: ["session", id] });
            navigate("/sessions");
        },
        onError: async (err) => {
            const apiErr = await unwrapError(err);
            showToast(`Cancel failed: ${apiErr.message}`, "error");
        },
    });
    if (detailQ.isLoading)
        return _jsx(SkeletonList, { rows: 4 });
    if (detailQ.error || !detailQ.data) {
        return _jsx(ErrorState, { message: "Failed to load session" });
    }
    const { session, slots, log } = detailQ.data;
    const credential = session.credentialId
        ? credsQ.data?.find((c) => c.id === session.credentialId)
        : undefined;
    // While credentials are still loading, show a neutral dash rather than the
    // misleading "(unknown)" — only fall back to "(unknown)" once the query has
    // resolved and the credential genuinely can't be found.
    const credentialLabel = session.credentialId
        ? credential?.label ?? (credsQ.isLoading ? "—" : "(unknown)")
        : "—";
    // Use the query's fetch time as "now" — a stable value across renders (the
    // render-phase purity rule forbids Date.now() here).
    const isPast = new Date(session.endTime).getTime() < detailQ.dataUpdatedAt;
    const cancelDisabled = isPast || cancel.isPending;
    const bookedCount = slots.filter((s) => s.status === "booked").length;
    const handleCancel = async () => {
        const message = bookedCount > 0
            ? `This will cancel ${bookedCount} booked court${bookedCount === 1 ? "" : "s"} with the venue (BeWell) — a real reservation that cannot be undone. The venue's cancellation policy may apply. This is the only action that cancels a booked court; editing or deleting the rule never does.`
            : "This session has no booked courts yet. Cancelling just removes the planned booking — nothing is reserved with the venue.";
        const ok = await confirm("Cancel this session?", message, {
            danger: true,
            confirmText: bookedCount > 0 ? `Cancel ${bookedCount} court${bookedCount === 1 ? "" : "s"}` : "Cancel session",
        });
        if (ok)
            cancel.mutate();
    };
    return (_jsxs("div", { children: [_jsx("div", { style: { marginBottom: 8 }, children: _jsx(Link, { to: "/sessions", style: {
                        color: colors.textSecondary,
                        textDecoration: "none",
                        fontSize: "0.85rem",
                    }, children: "\u2190 Back to sessions" }) }), _jsx(PageHeader, { title: formatFullTime(session.startTime, session.endTime), children: _jsxs(DangerButton, { onClick: () => void handleCancel(), disabled: cancelDisabled, children: [_jsx(Icon, { name: "x", size: 12 }), " Cancel session"] }) }), _jsxs(Card, { style: { marginBottom: 16 }, children: [_jsxs("div", { style: {
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            flexWrap: "wrap",
                            marginBottom: 8,
                        }, children: [_jsx(StatusChip, { status: session.status }), _jsx("strong", { style: { fontSize: "1rem" }, children: formatFullTime(session.startTime, session.endTime) })] }), _jsx("div", { style: {
                            fontSize: "0.82rem",
                            color: colors.textSecondary,
                            fontVariantNumeric: "tabular-nums",
                            marginBottom: 8,
                        }, children: formatSessionSubline(session.startTime, session.endTime, slots.length) }), _jsxs("div", { style: {
                            display: "grid",
                            gridTemplateColumns: "auto 1fr",
                            gap: "4px 12px",
                            fontSize: "0.85rem",
                            color: colors.textSecondary,
                        }, children: [_jsx("span", { children: "Venue" }), _jsx("span", { style: { color: colors.text }, children: session.venueId ? (_jsx(VenueChip, { venueId: session.venueId })) : (_jsx("span", { style: { color: colors.textTertiary }, children: "\u2014" })) }), _jsx("span", { children: "Credential" }), _jsx("span", { style: { color: colors.text }, children: session.credentialId ? (_jsx(CredentialChip, { label: credentialLabel, colorKey: session.credentialId })) : (_jsx("span", { style: { color: colors.textTertiary, fontStyle: "italic" }, children: "Picked when slot becomes bookable" })) }), _jsx("span", { children: "Courts" }), _jsxs("span", { style: { color: colors.text }, children: [session.bookedCount, " booked / ", slots.length, " total", session.plannedCount > 0
                                        ? ` · ${session.plannedCount} planned`
                                        : ""] }), _jsx("span", { children: "Bookable from" }), _jsx("span", { style: { color: colors.text }, children: new Date(session.bookableFrom).toLocaleString([], {
                                    month: "short",
                                    day: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                }) }), session.ruleId && (_jsxs(_Fragment, { children: [_jsx("span", { children: "From preference" }), _jsx("span", { style: { color: colors.text }, children: _jsx(Link, { to: `/rules/${session.ruleId}`, children: "View booking preference" }) })] }))] })] }), _jsxs(Card, { style: { marginBottom: 16, overflowX: "auto" }, children: [_jsx("div", { style: {
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                            color: colors.textSecondary,
                            marginBottom: 10,
                        }, children: "Courts" }), _jsx(CourtGrid, { courtCount: venueCourtCount(session.venueId), slots: slots.map((s) => ({
                            id: s.id,
                            startTime: s.startTime,
                            status: s.status === "booked" ? "booked" : s.eligible === false ? "unfulfilled" : "planned",
                            courtNumber: s.courtNumber ?? null,
                            locationId: s.locationId ?? null,
                            origin: s.origin,
                        })) }), _jsx(CourtGridKey, { style: { marginTop: 12 } })] }), session.status === "skipped" && session.skipReason && (_jsxs(Card, { style: {
                    marginBottom: 16,
                    background: colors.warningLight,
                    borderLeft: `4px solid ${colors.warning}`,
                }, children: [_jsxs("strong", { style: { display: "block", marginBottom: 4 }, children: [_jsx(Icon, { name: "alert-triangle", size: 14 }), " Session was skipped"] }), _jsx("span", { style: { color: colors.text, fontSize: "0.85rem" }, children: SKIP_REASON_TEXT[session.skipReason] ?? session.skipReason })] })), session.status === "unfulfillable" && (_jsxs(Card, { style: {
                    marginBottom: 16,
                    background: colors.dangerLight,
                    borderLeft: `4px solid ${colors.danger}`,
                }, children: [_jsxs("strong", { style: {
                            display: "block",
                            marginBottom: 4,
                            color: colors.danger,
                        }, children: [_jsx(Icon, { name: "alert-triangle", size: 14 }), " This session can't be booked"] }), _jsx("span", { style: { color: colors.text, fontSize: "0.85rem" }, children: "None of its time slots fit your current permitted windows. Adjust the underlying booking preference \u2014 the bot won't attempt this one." })] })), _jsx(Section, { title: "Activity", children: _jsx(Card, { children: _jsx(SessionTimeline, { log: log }) }) })] }));
}
